import React, { useState, useEffect } from 'react';
import { getMyProfile, updateMyProfile } from '../../services/therapistService';
import { listServices } from '../../services/serviceService';
import ZonePicker from '../shared/ZonePicker';

const ProfileForm = () => {
  const [profile, setProfile] = useState(null);
  const [services, setServices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    Promise.all([getMyProfile(), listServices()]).then(([p, s]) => {
      setProfile(p);
      setServices(s);
    });
  }, []);

  const toggleInArray = (field, value) => {
    setProfile(prev => {
      const current = prev[field] || [];
      const next = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [field]: next };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      const updated = await updateMyProfile({
        bio: profile.bio,
        photoUrl: profile.photoUrl,
        massageTypesOffered: profile.massageTypesOffered,
        venueTypesServed: profile.venueTypesServed,
        zonesServed: profile.zonesServed.map(z => z._id || z)
      });
      setProfile(updated);
      setMessage('Profile saved');
    } catch (err) {
      setMessage(err.response?.data?.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (!profile) return <p>Loading profile...</p>;

  return (
    <form onSubmit={handleSave} className="space-y-6 bg-white p-6 rounded-lg shadow">
      <div>
        <label className="block text-sm font-medium mb-1">Bio</label>
        <textarea
          value={profile.bio || ''}
          onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
          className="w-full p-3 border rounded-lg"
          rows={4}
        />
      </div>

      <div>
        <p className="text-sm font-medium mb-2">Massage types offered</p>
        <div className="flex flex-wrap gap-2">
          {services.map(service => (
            <button type="button" key={service._id}
              onClick={() => toggleInArray('massageTypesOffered', service.name)}
              className={`px-3 py-1 rounded-full border text-sm ${
                profile.massageTypesOffered?.includes(service.name) ? 'bg-black text-white' : 'bg-gray-100'
              }`}>
              {service.name}
            </button>
          ))}
          {services.length === 0 && (
            <p className="text-sm text-gray-400">No services have been set up by the admin yet.</p>
          )}
        </div>
      </div>

      <div>
        <p className="text-sm font-medium mb-2">Venue types served</p>
        <div className="flex gap-2">
          {['client_location', 'partner_venue'].map(v => (
            <button type="button" key={v}
              onClick={() => toggleInArray('venueTypesServed', v)}
              className={`px-3 py-1 rounded-full border text-sm ${
                profile.venueTypesServed?.includes(v) ? 'bg-black text-white' : 'bg-gray-100'
              }`}>
              {v === 'client_location' ? "Client's home/office" : 'Partner venue'}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-sm font-medium mb-2">Zones served</p>
        <p className="text-xs text-gray-500 mb-2">
          Pick the state and local government(s) you operate in. You can serve more than one.
        </p>
        <ZonePicker
          mode="multi"
          value={(profile.zonesServed || []).map(z => z._id || z)}
          onChange={(next) => setProfile(prev => ({ ...prev, zonesServed: next }))}
        />
      </div>

      {message && <p className="text-sm text-green-600">{message}</p>}

      <button type="submit" disabled={saving}
        className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 disabled:opacity-50">
        {saving ? 'Saving...' : 'Save profile'}
      </button>
    </form>
  );
};

export default ProfileForm;
